import { ReactNode, useCallback, useState, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  InvoiceFilterType,
  InvoiceModel,
  InvoiceSearchParams,
  InvoiceStatus,
  limitDateRange,
} from '@frontend/api-invoices';
import { PaymentQueries } from '@frontend/api-payments';
import { InfinitePaginatedList } from '@frontend/components';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { CollectPaymentMultiModalInstance, usePaymentsModalButton } from '@frontend/payments-collection-flow';
import { useLocationSurchargeState, PaymentsFeatureFlags, usePrintReceipt } from '@frontend/payments-hooks';
import { isPaidOrPendingInvoice, isFailedInvoice } from '@frontend/payments-invoice-calculations';
import {
  useInvoiceShallowStore,
  useExportInvoicesQuery,
  InvoiceCancelModal,
  useQueryAllInvoices,
  useQueryPaginatedInvoices,
} from '@frontend/payments-invoice-controller';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useWeTags } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { debounce } from '@frontend/timer';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  DeleteIcon,
  MarkIcon,
  PrintIcon,
  RefundIcon,
  UnmarkIcon,
  useModalControl,
  ExportIcon,
  ContentLoader,
  useAlert,
  UpdateIcon,
  ListRow,
  Avatar,
  Text,
  Chip,
  CheckIcon,
  MinusIcon,
  NakedButton,
  usePopoverMenu,
  PopoverMenu,
  PopoverMenuItem,
  NakedUl,
} from '@frontend/design-system';
import { isCareCreditPayment } from '../../../hooks/care-credit-account-utils';
import {
  FilterExportWarning,
  useInvoiceExportModalControl,
} from '../../../hooks/invoices/use-invoice-export-modal-control';
import { useCanDoActionsMulti } from '../../../hooks/use-can-do-actions-multi';
import { useCareCreditAccountLastFourMap } from '../../../hooks/use-care-credit-account-last-four-map';
import { useToggleInvoiceRecorded } from '../../../hooks/use-mark-invoice-recorded';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { getPaymentModelBrand } from '../../../utils/helpers';
import { getLatestRefund, calcAvailableToRefund } from '../../../utils/page-helpers/refunds';
import { RequestTableFilters } from '../../payment-requests/filters/request-table-filters';
import { PaymentRequestsListTable } from '../../payment-requests/table/list-table';
import { SendReceiptModal } from '../../Receipts/SendReceiptModal';
import { RefundModal } from '../../Refunds/RefundModal';
import { SkeletonListLoader } from '../../Shared/skeleton-list-loader';
import { InvoiceExportModal } from '../InvoiceExport/invoice-export-confirmation';
import { InvoiceStatusChip } from '../InvoiceStatusChip';

type InvoiceListPageProps = {
  handlePrint: () => void;
  onChangeFilter?: (filter: InvoiceFilterType) => void;
};

const FIELD_TO_ORDER: { [key: string]: InvoiceSearchParams['order'] } = {
  billedAmount: 'billedAmount',
  billedAt: 'billedAt',
  paidAmount: 'paidAmount',
  paidAt: 'paidAt',
};

const getInvoiceReceiptOptions = (invoice: InvoiceModel): { receiptType: 'refund' | 'payment'; refundId?: string } => ({
  receiptType: invoice.payment?.hasRefund ? 'refund' : 'payment',
  refundId: getLatestRefund(invoice)?.stripeId,
});

const hideSendReceipt = (invoice: InvoiceModel) => !invoice.payment || invoice.payment.status === 'FAILED';

export const InvoiceList = ({ handlePrint, onChangeFilter }: InvoiceListPageProps) => {
  const { t } = useTranslation('payments');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const navigate = useNavigate();
  const { selectedLocationIds: locationIds, accessibleLocationData } = useAppScopeStore();
  const { canExport, getCanDoActions } = useCanDoActionsMulti(locationIds, 'any');
  const [isPreparingPrint, setIsPreparingPrint] = useState(false); // State for print preparation

  const getLocationName = (locationId: string) => accessibleLocationData[locationId ?? '']?.name ?? '';

  //INVOICE PROVIDER STATE
  const { currentPage, numRows, setNumRows, setOrder, filter, setFilter, resetCurrentPage } = useInvoiceShallowStore(
    'currentPage',
    'numRows',
    'setNumRows',
    'setOrder',
    'filter',
    'setFilter',
    'resetCurrentPage'
  );

  const { getFeatureFlagValue } = useAppFlagStore();

  const showPayoutDescriptor = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.PayoutDescriptorColumn,
    locationIds: locationIds,
  });

  const { isAnyEnabled: hasSurchargesEnabled } = useLocationSurchargeState(locationIds);

  const { refetchCurrentPageFirst, hasMore, fetchNextPage, fetchPreviousPage, invoices, loading } =
    useQueryPaginatedInvoices({
      addPayoutDescriptor: showPayoutDescriptor,
    });

  // REFUNDS
  const alerts = useAlert();
  const { modalProps, triggerProps } = useModalControl({ disableReturnFocus: true });
  const sendReceiptModalControl = useModalControl();
  const {
    modalProps: collectPaymentModalProps,
    triggerProps: collectPaymentTriggerProps,
    getDisabledMessage,
  } = usePaymentsModalButton();
  const { onClick: openModal } = triggerProps;
  const [refundInvoice, setRefundInvoice] = useState<InvoiceModel>();
  const [sendReceiptInvoice, setSendReceiptInvoice] = useState<InvoiceModel>();
  const [collectPaymentInvoice, setCollectPaymentInvoice] = useState<string>();

  const issueRefund = (invoice: InvoiceModel) => {
    const { canRefund } = getCanDoActions([invoice.locationId]);
    if (!canRefund) {
      alerts.error(t(`You don't have permission to perform a refund`));
      return;
    }
    setRefundInvoice(invoice);
    openModal();
  };
  // CANCEL MODAL
  const { modalProps: confirmCancelModalProps, openModal: openConfirmCancelModal } = useModalControl();
  const [invoiceToCancel, setInvoiceToCancel] = useState<InvoiceModel | undefined>();
  const openCancel = (invoice: InvoiceModel) => {
    setInvoiceToCancel(invoice);
    openConfirmCancelModal();
  };

  // QUICK ACTIONS
  const { toggleRecorded } = useToggleInvoiceRecorded();
  // Modify useQueryAllInvoices to be controlled by isPreparingPrint
  const { loading: printLoading, isFetched: isPrindDataFetched } = useQueryAllInvoices({
    enable: isPreparingPrint, // Enable only when preparing print
    addPayoutDescriptor: showPayoutDescriptor,
  });
  const { invalidateUnrecordedCount } = PaymentQueries.usePaymentsInvalidation();
  const printReceipt = usePrintReceipt();
  const { exportInvoices, isExporting } = useExportInvoicesQuery();

  const { showInvoiceExportWarning, ...invoiceModalProps } = useInvoiceExportModalControl();

  // Effect to trigger handlePrint when data is ready
  useEffect(() => {
    // Check if we were preparing print, and the query is no longer loading/fetching
    if (isPreparingPrint && !printLoading && isPrindDataFetched) {
      handlePrint(); // Call the original print handler
      setIsPreparingPrint(false); // Reset the state
    }
  }, [isPreparingPrint, printLoading, handlePrint]);

  const handleExportInvoicesClick = useCallback(
    debounce(async () => {
      if (canExport) {
        const warnings: FilterExportWarning = {};

        if (filter.person) {
          warnings.searchParams = true;
        }
        // see if data will be truncated
        if (filter.dateRange && limitDateRange([filter.dateRange.start, filter.dateRange.end]).limited) {
          warnings.truncateDateRange = true;
        }
        // when other filters are applied with out a date range, they are truncated to the
        // default MAX range (3 months) because the result will show for all time
        else if (!filter.dateRange && Object.keys(filter).filter((value) => value !== 'dateRange').length > 0) {
          warnings.truncateDateRange = true;
        }

        // show warnings if any
        if (Object.keys(warnings).length > 0) {
          showInvoiceExportWarning(warnings);
        } else {
          exportInvoices();
        }
      } else {
        alerts.error(t('Payment admin role required'));
      }
    }, 1000),
    [filter]
  );

  const handleChangeFilter = useCallback(
    (filter: InvoiceFilterType) => {
      setFilter(filter);
      onChangeFilter?.(filter);
    },
    [onChangeFilter]
  );

  const handleCollectPaymentClick = (invoice: InvoiceModel) => {
    setCollectPaymentInvoice(invoice.id);
    collectPaymentTriggerProps.onClick();
  };

  const handleSendReceiptClick = (invoice: InvoiceModel) => {
    setSendReceiptInvoice(invoice);
    sendReceiptModalControl.openModal();
  };

  const closeSendReceiptModal = () => {
    sendReceiptModalControl.modalProps.onClose();
    setSendReceiptInvoice(undefined);
  };

  const isCancelledInvoice = (data: InvoiceModel) => data.status === InvoiceStatus.Canceled;

  const onRefreshClick = () => {
    refetchCurrentPageFirst();
    invalidateUnrecordedCount();
  };

  const RenderListItem = ({
    listItem: invoice,
    careCreditLastFourMap = {},
  }: {
    listItem: InvoiceModel;
    careCreditLastFourMap?: Record<string, string | undefined>;
  }) => {
    const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu<HTMLButtonElement>();
    const triggerProps = getTriggerProps();
    // the motivation for phrasing these all in the negative is to make sure the conditions match the `hide` prop in the table actions
    const { disabled } = usePaymentsModalButton({ locationIds: [invoice.locationId] });
    const hideRefund = !isPaidOrPendingInvoice(invoice) || calcAvailableToRefund(invoice) === 0;
    const hidePrintReceipt = !(
      invoice.status === InvoiceStatus.Paid ||
      invoice.status === InvoiceStatus.PartiallyPaid ||
      invoice.payment?.status === 'SUCCEEDED'
    );
    const hideMarkRecorded =
      !!invoice.payment?.recordedAt ||
      !(
        invoice.status === InvoiceStatus.Paid ||
        invoice.status === InvoiceStatus.PartiallyPaid ||
        invoice.payment?.status === 'SUCCEEDED'
      );
    const hideMarkUnrecorded = !invoice.payment?.recordedAt;
    const hideCollectPayment =
      !invoice.links?.payment || isPaidOrPendingInvoice(invoice) || isCancelledInvoice(invoice) || disabled;
    const hideCancel = isPaidOrPendingInvoice(invoice) || isCancelledInvoice(invoice);
    const showCompactListRow = useWeTags('compact-list-row');
    const isCareCredit = isCareCreditPayment(invoice.payment);
    const paymentLastFour = isCareCredit ? careCreditLastFourMap[invoice.id] : invoice.payment?.paymentDetails.lastFour;

    return (
      <ListRow
        as='div'
        css={{ gridTemplateAreas: '"lead content"' }}
        key={invoice.id}
        onClick={() => navigate({ to: '/payments/invoices/$id', params: { id: invoice.id } })}
      >
        <ListRow.Lead>
          <Avatar name={invoice.person.name} size={showCompactListRow ? 'small' : 'medium'} />
        </ListRow.Lead>
        <ListRow.Content>
          <ListRow.Content.Title
            as='div'
            css={[
              {
                alignItems: 'flex-start',
                display: 'flex',
                gap: theme.spacing(1),
                justifyContent: 'space-between',
                paddingRight: 0,
              },
              showCompactListRow && {
                alignItems: 'center',
                fontWeight: theme.font.weight.bold,
                color: theme.colors.neutral90,
                fontSize: theme.fontSize(16),
              },
            ]}
            isCompactMode={showCompactListRow}
          >
            <div css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
              <span>{invoice.person.name}</span>
              {invoice.payment && invoice.payment.recordedAt ? (
                <CheckIcon color='success' size={showCompactListRow ? 16 : 24} />
              ) : (
                <MinusIcon color='default' size={showCompactListRow ? 16 : 24} />
              )}
            </div>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              {invoice.locationId && showCompactListRow && (
                <Chip.Location
                  maxWidth=''
                  tooltipLabel={getLocationName(invoice.locationId)}
                  css={{ fontSize: theme.fontSize(12), gap: 0, width: 20, padding: theme.spacing(0.5), height: 16 }}
                ></Chip.Location>
              )}
              <NakedButton
                css={{ padding: theme.spacing(0.5), borderRadius: theme.borderRadius.small }}
                {...triggerProps}
              >
                <Icon name='more-small' />
              </NakedButton>

              <PopoverMenu {...getMenuProps()}>
                {!hideRefund && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 0 })}
                    Icon={RefundIcon}
                    trackingId='pay-portal-invoicelist-btn-refund'
                    onClick={() => {
                      close();
                      issueRefund(invoice);
                    }}
                  >
                    {t('Refund')}
                  </PopoverMenuItem>
                )}

                {!hideSendReceipt(invoice) && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 1 })}
                    Icon={() => <Icon name='payments-receipt' />}
                    trackingId='pay-portal-invoicelist-btn-sendreceipt'
                    onClick={() => {
                      close();
                      handleSendReceiptClick(invoice);
                    }}
                    disabled={isCareCreditPayment(invoice.payment)}
                    hoverLabel={
                      isCareCreditPayment(invoice.payment) ? t('Print receipts in your provider portal.') : undefined
                    }
                  >
                    {t('Send Receipt')}
                  </PopoverMenuItem>
                )}

                {!hidePrintReceipt && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 2 })}
                    Icon={PrintIcon}
                    trackingId='pay-portal-invoicelist-btn-print'
                    onClick={() => {
                      close();
                      printReceipt(invoice, getInvoiceReceiptOptions(invoice));
                    }}
                    disabled={isCareCreditPayment(invoice.payment)}
                    hoverLabel={
                      isCareCreditPayment(invoice.payment) ? t('Print receipts in your provider portal.') : undefined
                    }
                  >
                    {t('Print Receipt')}
                  </PopoverMenuItem>
                )}

                {!hideMarkRecorded && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 3 })}
                    Icon={MarkIcon}
                    trackingId='pay-portal-invoicelist-btn-unrecorded'
                    onClick={() => {
                      close();
                      toggleRecorded({
                        data: invoice,
                        paymentId: invoice.payment?.paymentId,
                        recordedAt: invoice.payment?.recordedAt,
                      });
                    }}
                  >
                    {t('Mark Recorded')}
                  </PopoverMenuItem>
                )}

                {!hideMarkUnrecorded && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 4 })}
                    Icon={MarkIcon}
                    trackingId='pay-portal-invoicelist-btn-unrecorded'
                    onClick={() => {
                      close();
                      toggleRecorded({
                        data: invoice,
                        paymentId: invoice.payment?.paymentId,
                        recordedAt: invoice.payment?.recordedAt,
                      });
                    }}
                  >
                    {t('Mark Unrecorded')}
                  </PopoverMenuItem>
                )}

                {!hideCollectPayment && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 5 })}
                    Icon={() => <Icon name='pay' />}
                    trackingId='pay-portal-invoicelist-collect-payment'
                    onClick={() => {
                      close();
                      handleCollectPaymentClick(invoice);
                    }}
                  >
                    {t('Collect Payment')}
                  </PopoverMenuItem>
                )}

                {!hideCancel && (
                  <PopoverMenuItem
                    {...getItemProps({ index: 6 })}
                    Icon={DeleteIcon}
                    trackingId='pay-portal-invoicelist-btn-cancel'
                    onClick={() => {
                      close();
                      openCancel(invoice);
                    }}
                  >
                    {t('Cancel Payment Request')}
                  </PopoverMenuItem>
                )}
              </PopoverMenu>
              {!showCompactListRow && <InvoiceStatusChip invoice={invoice} />}
            </div>
          </ListRow.Content.Title>
          {!showCompactListRow && (
            <ListRow.Content.Subtitle as='div'>
              {invoice.locationId && <Chip.Location maxWidth=''>{getLocationName(invoice.locationId)}</Chip.Location>}
            </ListRow.Content.Subtitle>
          )}
          <ListRow.Content.TertiaryTitle as='div'>
            <NakedUl
              css={css`
                display: flex;
                flex-direction: row;
                gap: ${theme.spacing(3)};
                li {
                  display: flex;
                  flex-direction: column;
                }
              `}
            >
              <li>
                <Text size='small' color='light'>
                  {t('Amount')}
                </Text>
                <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Text size={showCompactListRow ? 'medium' : 'large'}>
                    {formatCentsToCurrency(invoice.billedAmount)}
                  </Text>
                  {showCompactListRow && <InvoiceStatusChip invoice={invoice} size='tiny' />}
                </div>
              </li>
              {invoice.payment && (
                <li>
                  <Text size='small' color='light'>
                    {t('Payment Method')}
                  </Text>
                  <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                    <Text size={showCompactListRow ? 'medium' : 'large'}>{getPaymentModelBrand(invoice.payment)}</Text>
                    {!!paymentLastFour && <Text size={showCompactListRow ? 'medium' : 'large'}>{paymentLastFour}</Text>}
                  </div>
                </li>
              )}
              <li>
                <Text size='small' color='light'>
                  {t('Date Created')}
                </Text>
                <Text size={showCompactListRow ? 'medium' : 'large'}>
                  {formatDate(invoice.billedAt, 'MMM D, YYYY')}
                </Text>
              </li>
              {hasSurchargesEnabled && (
                <li>
                  <Text size='small' color='light'>
                    {t('Surcharge Amount')}
                  </Text>
                  <Text size={showCompactListRow ? 'medium' : 'large'}>
                    {formatCentsToCurrency(invoice.appliedSurchargeAmount ?? 0)}
                  </Text>
                </li>
              )}
            </NakedUl>
          </ListRow.Content.TertiaryTitle>
        </ListRow.Content>
      </ListRow>
    );
  };

  return (
    <>
      {isMobile ? (
        <MobileInvoiceList RenderListItem={RenderListItem} showPayoutDescriptor={showPayoutDescriptor} />
      ) : (
        <PaymentRequestsListTable
          locationIds={locationIds}
          paymentRequestsList={invoices}
          loading={loading}
          tableFilter={(modalProps, setShowNotification) => (
            <RequestTableFilters
              filter={filter}
              onChangeFilter={handleChangeFilter}
              modalProps={modalProps}
              setShowNotification={setShowNotification}
              setOrder={setOrder}
              resetCurrentPage={resetCurrentPage}
            />
          )}
          onSortChange={(res) => {
            resetCurrentPage();
            if (!res.length) {
              return;
            }
            const { id, value } = res[0] ?? {};
            const order = id && FIELD_TO_ORDER[id];
            if (order) {
              setOrder(`${value === 'asc' ? '' : '-'}${FIELD_TO_ORDER[id]}` as InvoiceSearchParams['order']);
            }
          }}
          tableActions={[
            {
              label: t('Export'),
              Icon: ExportIcon,
              onClick: handleExportInvoicesClick,
              disabled: !canExport,
            },
            {
              label: printLoading || isPreparingPrint ? t('Preparing for print') : t('Print'),
              Icon: PrintIcon,
              onClick: () => {
                // Only start preparing if not already loading/preparing
                if (!printLoading && !isPreparingPrint) {
                  setIsPreparingPrint(true);
                }
              },
              // Disable while preparing or fetching
              disabled: printLoading || isPreparingPrint,
            },
            {
              label: t('Refresh'),
              Icon: UpdateIcon,
              onClick: onRefreshClick,
            },
          ]}
          rowActions={{
            onRowClick: (invoice) => {
              navigate({ to: '/payments/invoices/$id', params: { id: invoice.id } });
            },
            // shouldHover: true,
            actions: [
              // PAYMENT ACTIONS
              {
                label: t('Refund'),
                Icon: RefundIcon,
                onClick: issueRefund,
                hide: (data) => !isPaidOrPendingInvoice(data) || calcAvailableToRefund(data) === 0,
                trackingId: 'pay-portal-invoicelist-btn-refund',
              },
              {
                label: t('Send Receipt'),
                Icon: () => <Icon name='payments-receipt' size={16} />,
                hide: hideSendReceipt,
                onClick: handleSendReceiptClick,
                trackingId: 'pay-portal-invoicelist-btn-sendreceipt',
                disabled: (data) => isCareCreditPayment(data.payment),
                hoverLabel: (data) => {
                  if (isCareCreditPayment(data.payment)) {
                    return t('Print receipts in your provider portal.');
                  }

                  return undefined;
                },
              },
              {
                label: t('Print Receipt'),
                Icon: PrintIcon,
                hide: (data) =>
                  !(
                    data.status === InvoiceStatus.Paid ||
                    data.status === InvoiceStatus.PartiallyPaid ||
                    data.payment?.status === 'SUCCEEDED'
                  ),
                onClick: (data) => printReceipt(data, getInvoiceReceiptOptions(data)),
                trackingId: 'pay-portal-invoicelist-btn-print',
                disabled: (data) => isCareCreditPayment(data.payment),
                hoverLabel: (data) => {
                  if (isCareCreditPayment(data.payment)) {
                    return t('Print receipts in your provider portal.');
                  }

                  return undefined;
                },
              },
              {
                label: t('Mark Recorded'),
                Icon: MarkIcon,
                hide: (data) =>
                  !!data.payment?.recordedAt ||
                  !(
                    data.status === InvoiceStatus.Paid ||
                    data.status === InvoiceStatus.PartiallyPaid ||
                    data.payment?.status === 'SUCCEEDED'
                  ),
                onClick: (data) =>
                  toggleRecorded({ data, paymentId: data.payment?.paymentId, recordedAt: data.payment?.recordedAt }),
                trackingId: 'pay-portal-invoicelist-btn-recorded',
              },
              {
                label: t('Mark Unrecorded'),
                Icon: UnmarkIcon,
                hide: (data) => !data.payment?.recordedAt,
                onClick: (data) =>
                  toggleRecorded({ data, paymentId: data.payment?.paymentId, recordedAt: data.payment?.recordedAt }),
                trackingId: 'pay-portal-invoicelist-btn-unrecorded',
              },
              // PAYMENT ACTIONS
              {
                label: (data) => getDisabledMessage(data.locationId).disabledMessage || t('Collect Payment'),
                Icon: () => <Icon name='pay' />,
                hide: (data) => !data.links?.payment || isPaidOrPendingInvoice(data) || isCancelledInvoice(data),
                onClick: (data) => handleCollectPaymentClick(data),
                disabled: (data) => !!getDisabledMessage(data.locationId).disabledMessage,
                trackingId: 'pay-portal-invoicelist-collect-payment',
                showLabelWhenDisabled: true,
              },
              {
                label: t('Cancel Payment Request'),
                Icon: DeleteIcon,
                hide: (data) => isPaidOrPendingInvoice(data) || isCancelledInvoice(data) || isFailedInvoice(data),
                onClick: (data) => openCancel(data),
                trackingId: 'pay-portal-invoicelist-btn-cancel',
              },
            ],
          }}
          searchConfig={{
            initialValue: filter.person || '',
            position: 'right',
            searchHandler: (searchText: string) => {
              const toSet = {
                ...filter,
                person: searchText.trim().length > 0 ? searchText : undefined,
              };

              setFilter(toSet);
              onChangeFilter?.(toSet);
            },
            placeholder: t('Search'),
            debounceDelay: 1000,
          }}
          paginationConfig={{
            page: currentPage,
            hasNext: !!hasMore,
            hasPrevious: currentPage !== 1,
            onNumRowsChange: (num) => {
              setNumRows(num);
              resetCurrentPage();
            },
            defaultRowsPerPage: numRows,
            rowsPerPageOptions: [10, 25, 50, 75, 100],
            handleChange: (prevOrNext: 'prev' | 'next') => {
              if (prevOrNext === 'prev') fetchPreviousPage();
              if (prevOrNext === 'next') fetchNextPage();
            },
          }}
        />
      )}
      <InvoiceCancelModal
        modalProps={confirmCancelModalProps}
        invoice={invoiceToCancel}
        onClose={confirmCancelModalProps.onClose}
      />
      {refundInvoice && <RefundModal invoice={refundInvoice} {...modalProps} />}
      {sendReceiptInvoice && (
        <SendReceiptModal
          invoice={sendReceiptInvoice}
          {...getInvoiceReceiptOptions(sendReceiptInvoice)}
          {...sendReceiptModalControl.modalProps}
          onClose={closeSendReceiptModal}
        />
      )}
      <InvoiceExportModal {...invoiceModalProps} onConfirm={exportInvoices} />
      {isExporting && <ContentLoader show={true} message={t('Preparing Export...')} />}
      {/* Show loader when preparing print data */}
      {isPreparingPrint && <ContentLoader show={true} message={t('Preparing print data...')} />}
      {collectPaymentInvoice && (
        <CollectPaymentMultiModalInstance
          modalProps={collectPaymentModalProps}
          collectProps={{
            invoiceId: collectPaymentInvoice,
            flow: 'pay',
          }}
        />
      )}
    </>
  );
};

const MobileInvoiceList = ({
  RenderListItem,
  showPayoutDescriptor,
}: {
  showPayoutDescriptor: boolean;
  RenderListItem: (props: {
    listItem: InvoiceModel;
    careCreditLastFourMap?: Record<string, string | undefined>;
  }) => ReactNode;
}) => {
  const { t } = useTranslation('payments');
  const { infiniteQueryProps } = useQueryPaginatedInvoices({
    addPayoutDescriptor: showPayoutDescriptor,
    select: (data) => ({
      pageParams: data.pageParams,
      pages: data.pages.map((page) => ({
        ...page,
        rows: page?.data?.invoices || [],
      })),
    }),
  });
  const mobileInvoices = useMemo(
    () => infiniteQueryProps.data?.pages.flatMap((page) => page.rows ?? []) ?? [],
    [infiniteQueryProps.data]
  );
  const careCreditLastFourMap = useCareCreditAccountLastFourMap(mobileInvoices);

  return infiniteQueryProps.isLoading ? (
    <SkeletonListLoader />
  ) : (
    <InfinitePaginatedList
      renderListItem={(props) => <RenderListItem {...props} careCreditLastFourMap={careCreditLastFourMap} />}
      emptyStateConfig={{ type: 'payments', header: t('No data to display') }}
      infiniteQueryProps={infiniteQueryProps}
    />
  );
};
